import React from "react";
import { PurchaseCreditSection } from "./PurchaseCreditSection";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useMutation } from "@tanstack/react-query";
import {
  cancelCartForCreditApi,
  getCreditPacksApi,
  lockSeatsForCreditApi,
  purchaseCreditApi,
} from "@/global/api/credit";
import { useAppStore } from "@/global/store";
import {
  CreditPackageSelectedState,
  selectCreditPackageSelected,
  selectCreditPackages,
  setCreditPackageSelected,
  setPurchaseCreditCart,
  setCreditPackages,
  purchaseCreditCart,
} from "@/global/store/data/purchaseCredit";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { selectUser, userState } from "@/global/store/data/user";
import { AuthCallback, useTicketmaster } from "@/global/hooks/useTicketmaster";
import {
  authOriginUIDs,
  authUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { parseApiErrorMessage } from "@/global/utils/utils";
import {
  PurchaseCreditCheckout,
  PurchaseCreditCheckoutModal,
} from "./PurchaseCreditCheckout";
import { PaymentData } from "../GenericPaymentForm/GenericPaymentForm";
import {
  PurchaseCreditSuccess,
  PurchaseCreditSuccessModal,
} from "./PurchaseCreditSuccess";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { decodeBase64String } from "@/global/utils/auth.utils";
import { useGetCreditReport } from "../PurchaseSeats/Checkout/Payment/PaymentDetails/useGetCreditReport";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { toast } from "react-toastify";
import { parse, format } from "date-fns";
import { BraintreeResponse, BraintreeSuccessResponse } from "@/types";
import axios from "axios";
import { endpoints } from "@/global/api/endpoints";
import { useBraintree } from "@/global/hooks/useBraintree";
import { routes } from "@/global/routes";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { findAndRetrieveEkataScoreApi } from "@/global/api/cart";
import { useTransactionErrorGenericModal } from "../PurchaseSeats/modals/TransactionErrorGenericModal";
import { useCheckout202ErrorHandler } from "@/shared/common/useCheckout202ErrorHandler";
import { useRepInfoState } from "@/global/context/RepInfoCheckerContainer";

export interface UrlPayload {
  authOrigin: string;
  tempUser?: {
    email: string;
    accountId: number;
  };
  selectedCreditPackage: CreditPackageSelectedState;
}

interface PurchaseCreditProps {
  authRedirectPath: string;
  successModalCloseRedirectPath?: string;
  skipRegister?: boolean;
}

export function PurchaseCredit(props: PurchaseCreditProps) {
  //-----------------App system----------------------
  const { dispatch, router } = useAppSystem();
  const {
    teamSchema,
    languages,
    platform,
    isBrainTreePaymentEnabled,
    uiConfigs,
  } = useTeamConfig();
  const { handleConnectTm } = useTicketmaster();
  const { handleLoginOkta } = useTicketsdotcom();
  const { shouldUseInternalLogin, handleInternalRedirect } = useInternalAuth();

  //-----------------Apis----------------------
  const creditReportApi = useGetCreditReport();
  const getCreditPackages = useMutation(getCreditPacksApi);
  const purchaseCreditCheckout = useMutation(purchaseCreditApi);
  const lockSeatsForCredit = useMutation(lockSeatsForCreditApi);
  const cancelCartForCredit = useMutation(cancelCartForCreditApi);
  const findAndRetrieveEkataScore = useMutation(findAndRetrieveEkataScoreApi);

  //-----------------Business States----------------------
  const urlPayload: UrlPayload | null = decodeBase64String(
    router?.query?.payload as string
  );
  const creditPackages = useAppStore(selectCreditPackages);
  const selectedCreditPackage = useAppStore(selectCreditPackageSelected);
  const user = useAppStore(selectUser);
  const [tempUserSession, setTempUserSession] = React.useState<
    UrlPayload["tempUser"] | null
  >(null);
  const purchaseCreditState = useAppStore(purchaseCreditCart);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [isCollapseOpen, setIsCollapseOpen] = React.useState(false);
  const { braintreePayment } = useBraintree();
  const checkoutCreditButtonText = uiConfigs?.purchaseCredit?.checkoutCreditButtonText || "Purchase Credits"; // prettier-ignore
  const checkoutCreditModalTitle =
    uiConfigs?.purchaseCredit?.checkoutCreditModalTitle || "Add Credit";
  const sortCreditPackagesBy =
    uiConfigs?.purchaseCredit?.sortCreditPackagesBy || "amount";
  const { repAccountId } = useRepInfoState();

  //-----------------Modal States----------------------
  const checkoutModal = useToggle();
  const successModal = useToggle();
  const errorModal = useToggle();
  const transactionErrorGenericModal = useTransactionErrorGenericModal();
  const { handleCreditCheckout202Error } = useCheckout202ErrorHandler();

  //-----------------Loading States----------------------
  const [selectCreditPackageLoading, setSelectCreditPackageLoading] =
    React.useState(false);

  const handleGetCreditPackages = async () => {
    try {
      const payload = { teamSchema };
      await getCreditPackages.mutateAsync(payload, {
        onSuccess: ({ data }) => {
          dispatch(
            setCreditPackages({
              packages: data,
              sortBy: sortCreditPackagesBy,
            })
          );
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectCreditPackage = async (pkg: CreditPackageSelectedState) => {
    try {
      setSelectCreditPackageLoading(true);
      dispatch(setCreditPackageSelected(pkg));
      if (!user) {
        const callback: AuthCallback = {
          pathname: props?.authRedirectPath,
          skipRegister: props?.skipRegister,
          payload: {
            authOrigin: authOriginUIDs.purchaseCredit,
            selectedCreditPackage: pkg,
          },
        };

        if (platform === vendorUUIDs.ticketmaster) {
          handleConnectTm({ callback });
          return;
        }

        if (platform === vendorUUIDs.tickesdotcom) {
          if (shouldUseInternalLogin) {
            const state = window.btoa(JSON.stringify({ callback }));
            return await handleInternalRedirect(
              routes.auth,
              state,
              authUIDs.login
            );
          }

          await handleLoginOkta({ callback });
          return;
        }
      }

      if (platform === vendorUUIDs.ticketmaster) {
        return checkoutModal.onOpen();
      }

      if (platform === vendorUUIDs.tickesdotcom) {
        try {
          return await handleLockSeatsForCredit(pkg);
        } catch (error) {
          console.log(error);
        } finally {
          setSelectCreditPackageLoading(false);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSelectCreditPackageLoading(false);
    }
  };

  const handleRemoveRouterPayload = () => {
    const query = router.query;
    if (query?.payload) {
      delete query?.payload;
      router.replaceShallow({ query });
    }
  };

  const onCloseSuccessModal = () => {
    dispatch(setCreditPackageSelected(null));
    successModal.onClose();
    if (props?.successModalCloseRedirectPath) {
      router.push({
        pathname: props?.successModalCloseRedirectPath,
        query: { teamSchema },
      });
    }
  };

  // prettier-ignore
  const onBraintreeSuccess = (response: BraintreeResponse): BraintreeSuccessResponse => {
    const paymentMethodNonce = response?.creditCards[0]?.nonce ?? undefined;
    const cardType = response?.creditCards[0]?.details?.cardType ?? undefined;
    return { paymentMethodNonce, cardType };
  };

  const handleSubmitCheckout = async (paymentData: PaymentData) => {
    setIsSubmitting(true);

    let payload = Object.create(null);
    payload.teamSchema = teamSchema;
    payload.creditPack = {
      amount: purchaseCreditState?.amount ?? selectedCreditPackage?.amount,
      accountGroupName: selectedCreditPackage?.accountGroupName,
      accountGroupId: selectedCreditPackage?.accountGroupId,
      id: selectedCreditPackage?.id,
    };
    payload.cardDetail = paymentData?.cardDetails;
    payload.savedCard = paymentData?.savedCard;
    if (!!tempUserSession) payload.user = tempUserSession;
    if (!!user && !tempUserSession) {
      payload.user = {
        accountId: user.accountId,
        email: user.email,
        userId: user.userId,
        accountGroupIds: user?.accountGroupIds,
        firstName: user?.firstName,
        lastName: user?.lastName,
        phoneNumber: user?.phoneNumber,
      };
    }

    payload.repAccountId = repAccountId || undefined;

    if (!!paymentData.cardDetails) {
      // ekata validation
      try {
        const { data: ekataResponse } =
          await findAndRetrieveEkataScore.mutateAsync({
            teamSchema,
            cardDetail: {
              address: paymentData.cardDetails.address,
              zipCode: paymentData.cardDetails.zipCode,
              firstName: paymentData.cardDetails.firstName,
              lastName: paymentData.cardDetails.lastName,
              city: paymentData.cardDetails.city,
              country: paymentData.cardDetails.country,
              state: paymentData.cardDetails.state,
            },
            user: { ...payload.user },
          });

        if (!ekataResponse?.isValid) {
          setIsSubmitting(false);
          return transactionErrorGenericModal.set({
            isOpen: true,
            errorMessage: ekataResponse?.message,
          });
        }
      } catch (error) {
        setIsSubmitting(false);
        return errorModal.onOpen();
      }
    }

    // tdc
    if (!!purchaseCreditState) {
      let isErrorOccurred = false;
      let braintreeSuccessResponse: BraintreeSuccessResponse | null = {
        cardType: null,
        paymentMethodNonce: null,
      };

      if (isCollapseOpen && isBrainTreePaymentEnabled && !!payload.cardDetail) {
        let paymentTokenData: any;
        try {
          const { data } = await axios.post(
            endpoints.getPaymentToken,
            {
              teamSchema,
              cartId: purchaseCreditState.cartId,
            },
            {
              headers: { Authorization: `Bearer ${user?.token}` },
            }
          );
          paymentTokenData = data;
        } catch (error: any) {
          console.log(error);
          errorModal.onOpen();
          setIsSubmitting(false);
          isErrorOccurred = true;
        }

        if (isErrorOccurred) return;

        const token = paymentTokenData?.paymentToken;
        const creditCard = {
          number: paymentData?.cardDetails?.cardNumber.replace(
            /(\d{4})(?=\d)/g,
            "$1 "
          ),
          expirationDate: paymentData?.cardDetails?.cardExpiry.replace(
            /^(\d{2})(\d{2})$/,
            "$1/$2"
          ),
          cvv: String(paymentData?.cardDetails?.cardCvc),
          billingAddress: {
            postalCode: String(paymentData?.cardDetails?.zipCode),
          },
        };

        if (token) {
          await braintreePayment({
            token,
            creditCard,
            onError: (error) => {
              console.log("error", error);
              errorModal.onOpen();
              setIsSubmitting(false);
              isErrorOccurred = true;
            },
            onSuccess: (response) =>
              (braintreeSuccessResponse = onBraintreeSuccess(response)),
          });
        }
      }

      if (isErrorOccurred) return;

      payload.cartId = purchaseCreditState?.cartId;
      payload.sessionId = purchaseCreditState?.sessionId;
      if (!!payload.cardDetail) {
        payload.cardDetail = {
          ...payload.cardDetail,
          ...braintreeSuccessResponse,
          cardExpiry: paymentData.cardDetails?.cardExpiry
            ? format(
                parse(paymentData.cardDetails?.cardExpiry!, "MMyy", new Date()),
                "yyyy-MM"
              )
            : undefined,
          country: "USA",
        };
      }
    }

    try {
      await purchaseCreditCheckout.mutateAsync(payload, {
        onSuccess: ({ data }: any) => {
          checkoutModal.onClose();
          if (data?.session) {
            dispatch(
              userState.actions.set({ teamSchema, session: data.session })
            );
          }
          successModal.onOpen();
        },
        onError: async (err) => {
          setIsSubmitting(false);
          const { is202Error } = await handleCreditCheckout202Error(err);
          if (is202Error) return checkoutModal.onClose();
          errorModal.onOpen();
        },
      });
      await creditReportApi.fetch();
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
      handleRemoveRouterPayload();
    }
  };

  const handleLockSeatsForCredit = async (pkg: CreditPackageSelectedState) => {
    try {
      const payload = {
        teamSchema,
        creditPack: {
          eventCode: pkg?.eventCode!,
          seatCount: pkg?.seatCount!,
          priceCode: pkg?.priceCode!,
          ticketTypeCode: pkg?.ticketTypeCode!,
          accountGroupId: Number(pkg?.accountGroupId!),
          accountGroupName: pkg?.accountGroupName!,
          id: pkg?.id!,
          title: pkg?.title!,
          amount: pkg?.amount!,
        },
      };
      const { data } = await lockSeatsForCredit.mutateAsync(payload);
      if (data) {
        dispatch(setPurchaseCreditCart(data));
        checkoutModal.onOpen();
      }
    } catch (error) {
      setSelectCreditPackageLoading(false);
      toast.error(parseApiErrorMessage(error));
      console.log(error);
    } finally {
      setSelectCreditPackageLoading(false);
    }
  };

  const handleCancelCart = async () => {
    try {
      if (platform === vendorUUIDs.tickesdotcom) {
        const payload = {
          teamSchema,
          cartId: purchaseCreditState?.cartId!,
          sessionId: purchaseCreditState?.sessionId!,
        };
        await cancelCartForCredit.mutateAsync(payload);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseCheckoutModal = () => {
    handleRemoveRouterPayload();
    checkoutModal.onClose();
    dispatch(setCreditPackageSelected(null));

    handleCancelCart();
  };

  React.useEffect(() => {
    (async () => {
      handleGetCreditPackages();
      if (urlPayload?.authOrigin == authOriginUIDs.purchaseCredit) {
        dispatch(setCreditPackageSelected(urlPayload?.selectedCreditPackage));
        setTempUserSession(urlPayload?.tempUser);
        if (platform === vendorUUIDs.ticketmaster) {
          checkoutModal.onOpen();
        }
        if (platform === vendorUUIDs.tickesdotcom) {
          await handleLockSeatsForCredit(urlPayload?.selectedCreditPackage);
        }
      }
    })();
  }, []);

  return (
    <>
      <NotificationModal
        isOpen={errorModal.isOpen}
        title="Error"
        description={
          transactionErrorGenericModal.errorMessage ||
          parseApiErrorMessage(purchaseCreditCheckout?.error)
        }
        primaryButtonText="Close"
        onClose={() => errorModal.onClose()}
        onClickPrimaryButton={() => errorModal.onClose()}
        hideSecondaryButton
      />
      <PurchaseCreditSuccessModal
        isOpen={successModal.open}
        onClose={onCloseSuccessModal}
        renderBody={() => (
          <PurchaseCreditSuccess
            amount={selectedCreditPackage?.amount}
            text="has been added to your account!"
          />
        )}
      />
      <PurchaseCreditCheckoutModal
        isOpen={checkoutModal.open}
        isSubmitting={purchaseCreditCheckout.isLoading || isSubmitting}
        onClose={onCloseCheckoutModal}
        buttonText={checkoutCreditButtonText}
        title={checkoutCreditModalTitle}
        renderBody={() => (
          <PurchaseCreditCheckout
            onSubmitCheckout={handleSubmitCheckout}
            isLoading={getCreditPackages.isLoading}
            creditPackages={creditPackages}
            selectedCreditPackage={selectedCreditPackage}
            tempUserSession={tempUserSession ?? undefined}
            onCloseCheckoutModal={onCloseCheckoutModal}
            setIsCollapseOpen={setIsCollapseOpen}
          />
        )}
      />
      <PurchaseCreditSection
        title={
          uiConfigs?.purchaseCredit?.hideSecondaryTitle
            ? undefined
            : languages?.purchaseCredit?.mainCardTitle || "Stock up on credit"
        }
        description={
          languages?.purchaseCredit?.mainCardDescription ||
          "Don’t know which games you’d like to go to? Top your account up by adding an access pass."
        }
        creditPackages={creditPackages}
        isLoading={getCreditPackages.isLoading || selectCreditPackageLoading}
        selectedCreditPackage={selectedCreditPackage}
        onSelectCreditPackage={handleSelectCreditPackage}
      />
    </>
  );
}
